// @flow
import React, {Fragment} from 'react';
import classNames from 'classnames';
import {isNumber} from 'lodash';
import Tooltip from '@homeaway/react-tooltip';
import GeoDistance from '../GeoDistance/GeoDistance';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import {DualPrice} from '../DualPrice/DualPrice';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import {FormattedMessage} from 'react-intl';
import RankedBadgesCollection from '../RankedBadgesCollection/RankedBadgesCollection';
import UrgencyMessage from '../UrgencyMessage/UrgencyMessage';
import BadgeTooltip from './ReviewBadgeTooltip';
import PriceSummary from '@homeaway/react-price-summary';
import Experiment, {On, Off} from '@homeaway/react-experiment';
import {
    VRBO_WEB_SERP_PRICE_TO_RIGHT,
    VRBO_OPAS_DUALPRICE_EU,
    VRBO_OPAS_DUALPRICE_US,
    VRBO_OPAS_DUALPRICE_TAXEXLUDED
} from '../../abacus/experiments';
import {PP_ACCEPTED} from '../../constants/badges';

import type {Node} from 'react';
import type {Props as HitInfoProps} from './HitInfo';
import type {Badge} from './HitInfo';

type Props = {
    rankedBadges: Badge[],
    superlativesBadges: Badge[],
    isPriceAccurateAllowed: boolean,
    freeCancellationBadge: Badge
} & HitInfoProps;

type IconText = {
    icon: string,
    message: string,
    messageValueType: string
};

export type UnitMessage = {
    iconText: ?IconText
};

/**
 * This is the goofy, non-badge-looking PremierPartner label that typically
 * appears over the rating stars. Extracted locally to make
 * `showIncludesTaxesAndFeesMessage` logic easier to read.
 */
const PremierPartnerLabel = ({rankedBadges, tooltipParentNode}) => {
    const premierPartnerBadge = rankedBadges.find(
        (badge) => badge.id === PP_ACCEPTED
    );
    if (!premierPartnerBadge) {
        return null;
    }

    return (
        <Tooltip
            tooltipType="popover"
            content={premierPartnerBadge.helpText}
            placement="bottom"
            parentNode={tooltipParentNode}
            interactiveContainer
        >
            <span
                className="small"
                data-wdio="premier-partner-badge"
                tabIndex="-1"
                aria-hidden="true"
            >
                {premierPartnerBadge.name}
            </span>
        </Tooltip>
    );
};

const hasSuperlativesBadges = (superlativesBadges): %checks =>
    !!superlativesBadges && !!superlativesBadges.length;

/**
 * Classnames' flow-typed definition doesn't export an input type, so we're
 * using the deprecated existential type (*) to work around writing our own.
 */
type SuperlativeProps = {
    isDualPrice: boolean,
    superlativesBadges: Badge[]
};
const Superlative = ({isDualPrice, superlativesBadges}: SuperlativeProps) => {
    const superlativeClass = classNames({
        'HitInfo__superlative small': true,
        HitInfo__dualPriceSuperlative: isDualPrice
    });
    if (!superlativesBadges[0].helpText) {
        return (
            <span className={superlativeClass} aria-hidden="true">
                {superlativesBadges[0].name}
            </span>
        );
    }

    return (
        <span>
            <span className={superlativeClass} aria-hidden="true">
                {superlativesBadges[0].name}
            </span>
            <BadgeTooltip helpText={superlativesBadges[0].helpText} />
        </span>
    );
};

/**
 * Has two different content configurations based on the UK-only
 * `showIncludesTaxesAndFeesMessage` prop - see HitInfo#mapStateToProps for the
 * exact business logic.
 *
 * In the `showIncludesTaxesAndFeesMessage` configuration, a disclaimer is
 * displayed underneath the price, forcing the superlative to move over above
 * the rating stars, which forces the Premier Partner badge up into the badges
 * row with the other badges (free cancellation, rare find, etc).
 */
export default function HitInfoDesktop({
    brand,
    rankedBadges,
    hitIndex,
    intl,
    isHovered,
    listing,
    isDatelessSearch,
    isMetric,
    fullLocale,
    superlativesBadges,
    listingHasSecondaryPrice,
    isPriceAccurateAllowed,
    isMultiUnitHit,
    freeCancellationBadge
}: Props) {
    let tooltipParentNode;
    const {
        instantBookable,
        propertyMetadata,
        geoDistance,
        unitMessage,
        unitMetadata
    } = listing;
    const headline = isMultiUnitHit
        ? (unitMetadata && unitMetadata.unitName) || ''
        : (propertyMetadata && propertyMetadata.headline) || '';
    /* istanbul ignore next */
    if (typeof window !== 'undefined') {
        tooltipParentNode = document.getElementsByTagName('body')[0];
    }
    let rateListing = filterGraphQlAnywhere(ratingFragments.listing, listing);
    rateListing.starSize = listingHasSecondaryPrice ? '12' : '16';

    const FooterExperiment = ({children}) => (
        <Experiment name={VRBO_WEB_SERP_PRICE_TO_RIGHT}>{children}</Experiment>
    );

    const SuperlativeBadges = ({isDualPrice}) => {
        if (!hasSuperlativesBadges(superlativesBadges)) {
            return null;
        }

        return (
            <Superlative
                isDualPrice={isDualPrice}
                superlativesBadges={superlativesBadges}
            />
        );
    };

    const SinglePrice = ({priceSuperlativeClass}) => {
        return (
            <div className={priceSuperlativeClass}>
                <SuperlativeBadges isDualPrice={false} />
                <div
                    className={classNames('HitInfo__price', {
                        'HitInfo__Price--condensed': listingHasSecondaryPrice
                    })}
                >
                    <Price
                        listing={filterGraphQlAnywhere(
                            priceFragments.listing,
                            listing
                        )}
                        useAverageLabel={isDatelessSearch}
                        fullLocale={fullLocale}
                        instantBookable={instantBookable}
                        intl={intl}
                        isPriceAccurateAllowed={isPriceAccurateAllowed}
                        isDatelessSearch={isDatelessSearch}
                    />
                    {listingHasSecondaryPrice && listing.priceSummarySecondary && (
                        <span className="PriceSummary__secondary">
                            <PriceSummary
                                priceSummary={listing.priceSummarySecondary}
                            />
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const DualPriceOn = () => {
        return (
            <On>
                <footer className="media-flex__footer">
                    <div className="HitInfo__badgeRatingGroup--old  HitInfo__badgeRatingGroup--left HitInfo__badgeRatingGroup--top">
                        <BadgeRatingGroup isDualPrice />
                    </div>
                    <div className="HitInfo__priceSuperlativeGroup--new HitInfo__badgeRatingGroup--right HitInfo__badgeRatingGroup--top">
                        <DualPrice listing={listing} intl={intl} isDesktop />
                    </div>
                </footer>
            </On>
        );
    };

    const DualPriceExperiment = () => {
        return (
            <Experiment name={VRBO_OPAS_DUALPRICE_US}>
                <DualPriceOn />
                <Off>
                    <Experiment name={VRBO_OPAS_DUALPRICE_EU}>
                        <DualPriceOn />
                        <Off>
                            <Experiment name={VRBO_OPAS_DUALPRICE_TAXEXLUDED}>
                                <DualPriceOn />
                                <Off>
                                    <footer className="media-flex__footer media-flex__footer--background hover-bg">
                                        <FooterExperiment>
                                            <On>
                                                <div className="HitInfo__badgeRatingGroup--new HitInfo__badgeRatingGroup--left HitInfo__badgeRatingGroup--bottom">
                                                    <BadgeRatingGroup
                                                        isDualPrice={false}
                                                    />
                                                </div>
                                                <SinglePrice priceSuperlativeClass="HitInfo__priceSuperlativeGroup--new HitInfo__priceSuperlativeGroup--right HitInfo__priceSuperlativeGroup--bottom" />
                                            </On>
                                            <Off>
                                                <SinglePrice priceSuperlativeClass="HitInfo__priceSuperlativeGroup--old HitInfo__priceSuperlativeGroup--left HitInfo__priceSuperlativeGroup--bottom" />
                                                <div className="HitInfo__badgeRatingGroup--old HitInfo__badgeRatingGroup--right HitInfo__badgeRatingGroup--bottom">
                                                    <BadgeRatingGroup
                                                        isDualPrice={false}
                                                    />
                                                </div>
                                            </Off>
                                        </FooterExperiment>
                                    </footer>
                                </Off>
                            </Experiment>
                        </Off>
                    </Experiment>
                </Off>
            </Experiment>
        );
    };

    const RankedBadgesGroup = () => {
        return (
            <Experiment name={VRBO_OPAS_DUALPRICE_US}>
                <On>
                    <RankedBadgesCollection
                        rankedBadges={rankedBadges}
                        tooltipParentNode={tooltipParentNode}
                        brand={brand}
                        isDesktop
                        isDualPrice
                    />
                </On>
                <Off>
                    <Experiment name={VRBO_OPAS_DUALPRICE_EU}>
                        <On>
                            <RankedBadgesCollection
                                rankedBadges={rankedBadges}
                                tooltipParentNode={tooltipParentNode}
                                brand={brand}
                                isDesktop
                                isDualPrice
                            />
                        </On>
                        <Off>
                            <RankedBadgesCollection
                                rankedBadges={rankedBadges}
                                tooltipParentNode={tooltipParentNode}
                                brand={brand}
                                isDesktop
                                isDualPrice={false}
                            />
                        </Off>
                    </Experiment>
                </Off>
            </Experiment>
        );
    };

    const BadgeRatingGroup = ({isDualPrice}: {isDualPrice: boolean}) => {
        const ratingClass = classNames({
            HitInfo__rating: true,
            HitInfo__dualPriceRating: isDualPrice,
            'HitInfo__dualPriceRating--margin-top':
                isDualPrice && !hasSuperlativesBadges(superlativesBadges)
        });

        return (
            <Fragment>
                {isDualPrice && <SuperlativeBadges isDualPrice />}
                {!isDualPrice && (
                    <PremierPartnerLabel
                        rankedBadges={rankedBadges}
                        tooltipParentNode={tooltipParentNode}
                    />
                )}
                <div className={ratingClass}>
                    <Rating listing={rateListing} isDualPrice={isDualPrice} />
                </div>
            </Fragment>
        );
    };

    return (
        <Fragment>
            <div className="media-flex__body">
                <UrgencyMessage
                    message={unitMessage}
                    freeCancellationBadge={freeCancellationBadge}
                />
                <h2 className="HitInfo__headline hover-text" aria-hidden="true">
                    {headline}
                </h2>

                {isNumber(hitIndex) && (
                    <span className="sr-only">
                        <FormattedMessage
                            id="property"
                            description="Property"
                            defaultMessage="Property"
                        >
                            {(txt: Node): Node => (
                                <Fragment>
                                    {txt} {hitIndex + 1}
                                    {':'} {headline}
                                </Fragment>
                            )}
                        </FormattedMessage>
                    </span>
                )}

                <div className="HitInfo__details">
                    <Details
                        listing={listing}
                        isMetric={isMetric}
                        locale={intl.locale}
                    />
                </div>

                {geoDistance && (
                    <GeoDistance
                        brand={brand}
                        geoDistance={geoDistance}
                        isHighlighted={isHovered}
                    />
                )}
                <RankedBadgesGroup />
            </div>
            <DualPriceExperiment />
        </Fragment>
    );
}
